/**
 * Status Badge Component
 * Displays order status with appropriate styling and optional hover effects.
 * 
 * Enhanced version with "Response Received" indicator support and
 * visual feedback during status updates.
 * 
 * Extracted from App.js for better code organization.
 */

import React from 'react';
import { Badge } from './ui/badge';
import { CheckCircle } from 'lucide-react';

// Status configuration with colors and labels
const STATUS_CONFIG = {
  'pending': { color: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-600 hover:text-white', label: 'Pending' },
  'open': { color: 'bg-blue-100 text-blue-800 hover:bg-blue-600 hover:text-white', label: 'Open' },
  'in_progress': { color: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-600 hover:text-white', label: 'Pending' },
  'clarification': { color: 'bg-purple-100 text-purple-800 hover:bg-purple-600 hover:text-white', label: 'Clarification' },
  'clarification_received': { color: 'bg-purple-100 text-purple-800 hover:bg-purple-600 hover:text-white', label: 'Clarification Received' },
  'mail_away': { color: 'bg-cyan-100 text-cyan-800 hover:bg-cyan-600 hover:text-white', label: 'Mail-Away' },
  'mail_away_pending': { color: 'bg-blue-600 text-white hover:bg-blue-700', label: 'Mail-Away' }, // Blue badge for action required
  'mail_away_received': { color: 'bg-cyan-100 text-cyan-800 hover:bg-cyan-600 hover:text-white', label: 'Mail-Away Received' },
  'qc_review': { color: 'bg-violet-100 text-violet-800 hover:bg-violet-600 hover:text-white', label: 'QC Review' },
  // Feb 2026 — QC Workflow Phase 1: DB status remains `pending_correction`
  // for backwards-compatible reporting/integrations; user-facing label is
  // surfaced as "Correction Required" everywhere (badge, KPI, tab).
  'pending_correction': { color: 'bg-rose-100 text-rose-800 hover:bg-rose-600 hover:text-white', label: 'Correction Required' },
  // TYPING-001 (Aug 2026) — Typing production stage before final completion
  'typing': { color: 'bg-cyan-100 text-cyan-800 hover:bg-cyan-600 hover:text-white', label: 'Typing' },
  'complete': { color: 'bg-green-100 text-green-800 hover:bg-green-600 hover:text-white', label: 'Complete' },
  'cancelled': { color: 'bg-red-100 text-red-800 hover:bg-red-600 hover:text-white', label: 'Cancelled' },
  'created': { color: 'bg-gray-100 text-gray-800 hover:bg-gray-600 hover:text-white', label: 'Created' }
};

/**
 * Check if order has any pending (unconfirmed) Mail-Away requests
 * Used to determine if blue "Action Required" badge should be shown
 * 
 * P1 Workflow Update:
 * - Email auto-sends when status → mail_away
 * - Blue badge shows until Team Leader/Admin clicks "Mark Sent"
 * 
 * Returns TRUE (show blue badge) if:
 * 1. Order is in mail_away status AND mail_away_requests array is empty
 * 2. OR any ACTIVE request has status 'sent' but NOT confirmed (confirmed_at is null)
 * 3. OR any ACTIVE request has status 'not_sent'
 *
 * Jul 2026 — Cycle 2 badge fix (Fairhaven UAT, TF-20260626-44052):
 *   Prior-cycle rows (i.e., rows whose request_id appears as
 *   `previous_request_id` on any newer row) are SUPERSEDED and MUST NOT
 *   contribute to the blue-badge decision. Otherwise a Cycle 1 row that
 *   was never Mark-Sent-confirmed (typically because Phase 23 blocked
 *   the operator from doing so at the time) keeps the badge blue forever
 *   even after the operator confirms the current Cycle 2. The Order
 *   Summary popup hides the superseded row (correctly), so operators
 *   have no UI affordance to confirm it — the badge must therefore
 *   ignore superseded rows too, matching the popup's own visibility rule.
 */
const hasUnconfirmedMailAwayRequests = (orderData) => {
  if (!orderData) return false;
  
  const requests = orderData.mail_away_requests || [];
  
  // Case 1: Order is in mail_away status but NO requests exist yet
  if (requests.length === 0) {
    return true; // Show blue badge - action required
  }
  
  // Jul 2026 — Skip superseded prior-cycle rows so only the LATEST cycle
  // per jurisdiction contributes to the badge decision.
  const supersededIds = new Set(
    requests
      .map((r) => r && r.previous_request_id)
      .filter(Boolean)
  );
  const activeRequests = requests.filter(
    (r) => !supersededIds.has(r && r.request_id)
  );
  
  // Edge case: everything is superseded (shouldn't happen, but guard anyway)
  // → nothing active to confirm, treat as fully confirmed.
  if (activeRequests.length === 0) return false;
  
  // Case 2: Check if any ACTIVE request is either:
  // - status 'not_sent' (waiting for auto-send)
  // - status 'sent'/'awaiting_response' but NOT confirmed
  return activeRequests.some(req => {
    const status = req.status;
    const isConfirmed = !!req.confirmed_at;
    
    // Not sent yet - needs action
    if (status === 'not_sent') return true;
    
    // Sent but not confirmed - needs Team Leader/Admin to click "Mark Sent"
    if ((status === 'sent' || status === 'awaiting_response') && !isConfirmed) return true;
    
    return false;
  });
};

/**
 * Helper function: Normalize status display for Order Desk persona ONLY
 * Maps legacy "created" status → "Open" for Order Desk UI consistency
 */
const normalizeStatusForOrderDesk = (status, userRole) => {
  if (userRole === 'order_desk' && status?.toLowerCase() === 'created') {
    return 'open';
  }
  return status;
};

/**
 * StatusBadge Component
 * 
 * @param {string} status - Order status
 * @param {boolean} disableHover - Disable hover effects
 * @param {string} userRole - User's role for status normalization
 * @param {Object} orderData - Order data for additional indicators
 */
export const StatusBadge = ({ 
  status, 
  disableHover = false, 
  userRole = null, 
  orderData = null 
}) => {
  // Show "Updating..." indicator if order is being updated
  if (orderData?._updating) {
    return (
      <div className="inline-flex items-center gap-2">
        <Badge className="bg-gray-100 text-gray-600 text-xs px-2 py-1 font-medium border whitespace-nowrap animate-pulse">
          <span className="inline-flex items-center gap-1">
            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Updating...
          </span>
        </Badge>
      </div>
    );
  }
  
  // Apply Order Desk-only status normalization
  let normalizedStatus = normalizeStatusForOrderDesk(status, userRole);
  
  // P1 FIX: Check if Mail-Away status should show blue "Action Required" badge
  // If order has unconfirmed Mail-Away requests (sent but not marked), use blue badge
  if (normalizedStatus?.toLowerCase() === 'mail_away' && hasUnconfirmedMailAwayRequests(orderData)) {
    normalizedStatus = 'mail_away_pending';
  }
  
  const config = STATUS_CONFIG[normalizedStatus?.toLowerCase()] || { 
    color: 'bg-gray-100 text-gray-800', 
    label: normalizedStatus || 'Unknown' 
  };

  // MLS-PARTIAL-GREEN (Aug 2026): for dual MLS orders, when EXACTLY ONE
  // component is complete, keep the canonical status TEXT but render the
  // badge with the green treatment — the TL instantly sees one side of the
  // dual order is already done. Once BOTH components finish, downstream
  // statuses (QC Review, Correction Required, Complete) take over with
  // their normal styling, so green never becomes ambiguous.
  const _cs = orderData?.components
    ? { tax: orderData.components.tax?.status, mls: orderData.components.mls?.status }
    : orderData?.componentStatuses;
  const _isDual = !!(_cs && _cs.tax && _cs.mls);
  const _doneComps = _isDual ? ['tax', 'mls'].filter((c) => _cs[c] === 'complete') : [];
  const partialDualComplete = _isDual && _doneComps.length === 1 &&
    !['qc_review', 'pending_correction', 'typing', 'complete', 'cancelled'].includes(normalizedStatus?.toLowerCase());
  const badgeColor = partialDualComplete
    ? 'bg-green-100 text-green-800 border-green-300 hover:bg-green-600 hover:text-white'
    : config.color;
  const partialTitle = partialDualComplete
    ? `${_doneComps[0] === 'tax' ? 'Tax Search' : 'MLS Search'} component complete — ${_doneComps[0] === 'tax' ? 'MLS Search' : 'Tax Search'} still in progress`
    : undefined;
  
  // Check if response has been received for mail_away ONLY
  // Note: clarification_received has its own status, no need for separate indicator
  const showReceivedBadge = orderData && (
    (normalizedStatus?.toLowerCase() === 'mail_away' && orderData?.mail_away_received)
  );
  
  // Add pointer-events-none to disable hover
  const hoverClass = disableHover ? 'pointer-events-none' : '';
  
  return (
    <div className="inline-flex items-center gap-2">
      <Badge 
        className={`${badgeColor} text-xs px-2 py-1 font-medium border whitespace-nowrap transition-colors cursor-pointer ${hoverClass}`}
        data-testid={`status-badge-${normalizedStatus?.toLowerCase() || 'unknown'}`}
        data-partial-complete={partialDualComplete ? 'true' : undefined}
        title={partialTitle}
      >
        {config.label}
      </Badge>
      {showReceivedBadge && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-300 whitespace-nowrap">
          <CheckCircle size={12} />
          Response Received
        </span>
      )}
    </div>
  );
};

// Export status configuration for use in other components
export { STATUS_CONFIG, normalizeStatusForOrderDesk };

export default StatusBadge;
